import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Shield,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Eye,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  BarChart3,
  Filter,
  Search,
  ExternalLink,
  History,
  User,
  Calendar,
  Building2,
  DollarSign,
  Plus,
  Edit2,
  Save,
  Trash2,
  Copy,
  CopyPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import ROSManualDialog from '../components/ros/ROSManualDialog';

const ESTADO_CONFIG = {
  BORRADOR: { color: 'bg-slate-100 text-slate-700', icon: FileText },
  ENVIANDO_UIF: { color: 'bg-indigo-100 text-indigo-700', icon: RefreshCw },
  EN_REVISION: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  PENDIENTE_APROBACION: { color: 'bg-purple-100 text-purple-700', icon: Clock },
  APROBADO: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  RECHAZADO: { color: 'bg-red-100 text-red-700', icon: XCircle },
  ENVIADO: { color: 'bg-blue-100 text-blue-700', icon: Send },
  ARCHIVADO: { color: 'bg-gray-100 text-gray-700', icon: FileText },
};

const ESTADO_LABELS = {
  BORRADOR: 'Borrador',
  ENVIANDO_UIF: 'Enviando a UIF...',
  EN_REVISION: 'En Revisión',
  PENDIENTE_APROBACION: 'Pendiente Aprobación',
  APROBADO: 'Aprobado',
  RECHAZADO: 'Rechazado',
  ENVIADO: 'Enviado a UIF',
  ARCHIVADO: 'Archivado',
};

// Estados de CASOS para el filtro del modal de generación
const CASE_ESTADOS = [
  { value: 'ABIERTO', label: 'Abierto' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'EN_INVESTIGACION', label: 'En Investigación' },
  { value: 'EN_REVISION', label: 'En Revisión' },
  { value: 'ESCALADO', label: 'Escalado' },
  { value: 'ESPERANDO_COMPLIANCE', label: 'Esperando Compliance' },
  { value: 'BLOQUEO_PREVENTIVO', label: 'Bloqueo Preventivo' },
  { value: 'EN_VIGILANCIA', label: 'En Vigilancia' },
  { value: 'CERRADO', label: 'Cerrado' },
];

const formatCurrency = (value, currency = 'ARS') => {
  if (!value) return '$0';
  return new Intl.NumberFormat('es-AR', { 
    style: 'currency', 
    currency: currency,
    maximumFractionDigits: 0 
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function ROSReports() {
  const navigate = useNavigate();
  const [manualOpen, setManualOpen] = useState(false);
  const [sourceRos, setSourceRos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rosList, setRosList] = useState([]);
  const [stats, setStats] = useState(null);
  const [tiposRos, setTiposRos] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selected ROS for detail view
  const [selectedRos, setSelectedRos] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Approval dialog
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalComment, setApprovalComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Send to UIF dialog
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [numeroUif, setNumeroUif] = useState('');

  // Generate ROS dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [cases, setCases] = useState([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [casesLoading, setCasesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genEstadoFiltro, setGenEstadoFiltro] = useState('all');
  const [selectedGenCaseIds, setSelectedGenCaseIds] = useState({});
  const [bulkGenerating, setBulkGenerating] = useState(false);
  // Envío masivo a UIF
  const [selectedRosIds, setSelectedRosIds] = useState({});
  const [bulkSending, setBulkSending] = useState(false);
  // Progreso del envío masivo (barra)
  const [bulkProgressOpen, setBulkProgressOpen] = useState(false);
  const [bulkProgressItems, setBulkProgressItems] = useState([]);
  const [bulkMarking, setBulkMarking] = useState(false);
  const [cerrandoCubiertos, setCerrandoCubiertos] = useState(false);
  // Cierre definitivo (modal de opciones + progreso)
  const [cierreModalOpen, setCierreModalOpen] = useState(false);
  const [cierreAccion, setCierreAccion] = useState('ambas');
  const [cierreRunning, setCierreRunning] = useState(false);
  const [cierreProgressOpen, setCierreProgressOpen] = useState(false);
  const [cierreProgressItems, setCierreProgressItems] = useState([]);

  // Edit formulario UIF
  const [editingUif, setEditingUif] = useState(false);
  const [uifForm, setUifForm] = useState({});

  useEffect(() => {
    loadData();
    loadTiposRos();
  }, []);

  useEffect(() => {
    loadRosList();
  }, [filtroEstado, filtroTipo]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadRosList(), loadStats()]);
    } finally {
      setLoading(false);
    }
  };

  const loadRosList = async () => {
    try {
      const params = {};
      if (filtroEstado && filtroEstado !== 'all') params.estado = filtroEstado;
      if (filtroTipo && filtroTipo !== 'all') params.tipo = filtroTipo;
      
      const res = await api.get('/ros', { params });
      setRosList(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar ROS');
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/ros/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadTiposRos = async () => {
    try {
      const res = await api.get('/ros/tipos');
      setTiposRos(res.data);
    } catch (error) {
      console.error('Error loading tipos:', error);
    }
  };

  const handleViewRos = async (rosId) => {
    try {
      const res = await api.get(`/ros/${rosId}`);
      setSelectedRos(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar ROS');
    }
  };

  const handleSubmitForApproval = async (rosId) => {
    try {
      await api.post(`/ros/${rosId}/submit`);
      toast.success('ROS enviado para aprobación');
      loadRosList();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar para aprobación');
    }
  };

  const handleApprovalDecision = async () => {
    if (!selectedRos) return;
    if (approvalAction === 'RECHAZADO' && !approvalComment.trim()) {
      toast.error('El motivo del rechazo es obligatorio');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/ros/${selectedRos.id}/decide`, {
        decision: approvalAction,
        comentario: approvalComment
      });
      toast.success(approvalAction === 'APROBADO' ? 'ROS aprobado' : 'ROS rechazado');
      setShowApprovalDialog(false);
      setApprovalComment('');
      loadRosList();
      loadStats();
      
      // Reload detail if open
      if (showDetailDialog) {
        const res = await api.get(`/ros/${selectedRos.id}`);
        setSelectedRos(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar decisión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendToUIF = async () => {
    if (!selectedRos) return;
    
    setSubmitting(true);
    try {
      await api.post(`/ros/${selectedRos.id}/send-uif`, {
        numero_uif: numeroUif,
        fecha_envio_uif: new Date().toISOString()
      });
      toast.success('ROS marcado como enviado a UIF');
      setShowSendDialog(false);
      setNumeroUif('');
      loadRosList();
      loadStats();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar a UIF');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRos = async (rosId) => {
    if (!window.confirm(`¿Eliminar el ROS ${rosId}? Se eliminará también la referencia en el caso vinculado.`)) return;
    try {
      await api.delete(`/ros/${rosId}`);
      toast.success('ROS eliminado');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error eliminando');
    }
  };

  const handleDuplicarRos = async (rosId) => {
    try {
      const res = await api.get(`/ros/${rosId}`);
      setSourceRos(res.data);
      setManualOpen(true);
    } catch (e) {
      toast.error('No se pudo cargar el ROS para duplicar');
    }
  };

  // ====== Generate ROS ======
  // Generate ROS state
  const [rosTemplates, setRosTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');

  const openGenerateDialog = async () => {
    setShowGenerateDialog(true);
    setCaseSearch('');
    setSelectedTemplate('');
    setSelectedCaseId('');
    setSelectedGenCaseIds({});
    setGenEstadoFiltro('all');
    try {
      const tplRes = await api.get('/ros-config/templates');
      setRosTemplates(tplRes.data || []);
    } catch { toast.error('Error cargando datos'); }
  };

  // Búsqueda de casos contra el backend (busca en TODA la base, no solo 50 locales)
  useEffect(() => {
    if (!showGenerateDialog) return;
    const t = setTimeout(async () => {
      setCasesLoading(true);
      try {
        const params = { search: caseSearch || undefined, limit: 50 };
        if (genEstadoFiltro && genEstadoFiltro !== 'all') params.estado = genEstadoFiltro;
        const res = await api.get('/cases', { params });
        const d = res.data;
        setCases(Array.isArray(d) ? d : (d.items || d.cases || []));
      } catch { /* noop */ } finally { setCasesLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [caseSearch, genEstadoFiltro, showGenerateDialog]);

  const handleGenerateRos = async (casoId) => {
    setGenerating(true);
    try {
      const res = await api.post(`/ros/generar-desde-caso/${casoId}`, {
        template_id: selectedTemplate || undefined,
      });
      toast.success(`ROS ${res.data.id} generado correctamente`);
      setShowGenerateDialog(false);
      navigate(`/m/gestion-de-casos/ros-detalle/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando ROS');
    } finally { setGenerating(false); }
  };

  const toggleGenCase = (id) => setSelectedGenCaseIds((p) => ({ ...p, [id]: !p[id] }));
  const genSelectedIds = Object.keys(selectedGenCaseIds).filter((k) => selectedGenCaseIds[k]);

  const handleBulkGenerate = async () => {
    if (genSelectedIds.length === 0) { toast.error('Seleccioná al menos un caso'); return; }
    setBulkGenerating(true);
    try {
      const res = await api.post('/ros/generar-desde-caso-bulk', {
        caso_ids: genSelectedIds,
        template_id: selectedTemplate && selectedTemplate !== 'none' ? selectedTemplate : '',
      });
      const { creados = [], errores = [] } = res.data || {};
      toast.success(`${creados.length} ROS generados${errores.length ? ` · ${errores.length} con error` : ''}`);
      if (errores.length) {
        errores.slice(0, 3).forEach((e) => toast.error(`${e.caso_id?.slice(0, 8)}: ${e.error}`));
      }
      setShowGenerateDialog(false);
      setSelectedGenCaseIds({});
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la generación masiva');
    } finally { setBulkGenerating(false); }
  };

  // ====== Envío masivo a UIF ======
  const toggleRosSel = (id) => setSelectedRosIds((p) => ({ ...p, [id]: !p[id] }));
  const rosSelectedIds = Object.keys(selectedRosIds).filter((k) => selectedRosIds[k]);

  const handleBulkSend = async () => {
    if (rosSelectedIds.length === 0) { toast.error('Seleccioná al menos un ROS'); return; }
    if (!window.confirm(`Vas a enviar ${rosSelectedIds.length} ROS a la UIF (Playwright, se procesan de a uno). Los que no tengan el formulario completo se omiten. ¿Continuar?`)) return;
    setBulkSending(true);
    try {
      const res = await api.post('/ros/enviar-uif-bulk', { ros_ids: rosSelectedIds });
      const { encolados = [], omitidos = [] } = res.data || {};
      toast.success(`${encolados.length} ROS en cola de envío${omitidos.length ? ` · ${omitidos.length} omitidos` : ''}`);
      if (omitidos.length) omitidos.slice(0, 4).forEach((o) => toast.warning(`${o.ros_id}: ${o.motivo}`));
      setSelectedRosIds({});
      loadRosList();
      if (encolados.length) {
        // Abrir barra de progreso del envío masivo
        setBulkProgressItems(encolados.map((id) => ({ ros_id: id, estado: 'ENVIANDO_UIF', progreso: null })));
        setBulkProgressOpen(true);
        const poll = setInterval(async () => {
          try {
            const results = await Promise.all(
              encolados.map((id) =>
                api.get(`/ros/${id}/uif-progreso`).then((r) => ({ ros_id: id, ...r.data })).catch(() => ({ ros_id: id }))
              )
            );
            setBulkProgressItems(results.map((r) => ({
              ros_id: r.ros_id, estado: r.estado, progreso: r.uif_progreso || null,
              nro: r.nro_operacion_uif || '',
            })));
            const allDone = results.every((r) => r.estado && r.estado !== 'ENVIANDO_UIF');
            if (allDone) { clearInterval(poll); loadRosList(); }
          } catch { /* ignore */ }
        }, 2500);
        setTimeout(() => { clearInterval(poll); loadRosList(); }, 600000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar a UIF');
    } finally { setBulkSending(false); }
  };

  const copyCvu = (cvu) => {
    if (!cvu) { toast.error('Este ROS no tiene CVU'); return; }
    navigator.clipboard.writeText(String(cvu)).then(
      () => toast.success(`CVU copiado: ${cvu}`),
      () => toast.error('No se pudo copiar')
    );
  };

  const handleBulkMarkSent = async () => {
    if (rosSelectedIds.length === 0) { toast.error('Seleccioná al menos un ROS'); return; }
    if (!window.confirm(`Marcar ${rosSelectedIds.length} ROS como ENVIADOS/completados en la UIF?`)) return;
    setBulkMarking(true);
    try {
      const res = await api.post('/ros/marcar-enviado-bulk', { ros_ids: rosSelectedIds });
      const { marcados = [], omitidos = [] } = res.data || {};
      toast.success(`${marcados.length} ROS marcados como enviados${omitidos.length ? ` · ${omitidos.length} omitidos` : ''}`);
      if (omitidos.length) omitidos.slice(0, 4).forEach((o) => toast.warning(`${o.ros_id}: ${o.motivo}`));
      setSelectedRosIds({});
      loadRosList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al marcar como enviados');
    } finally { setBulkMarking(false); }
  };

  const handleCerrarCubiertos = async () => {
    const ids = rosSelectedIds.length > 0 ? rosSelectedIds : null;
    setCerrandoCubiertos(true);
    try {
      const prev = await api.post('/ros/cerrar-casos-cubiertos', { ros_ids: ids, dry_run: true });
      const n = prev.data?.total || 0;
      if (n === 0) { toast.info('No hay otros casos abiertos del mismo cliente para cerrar'); return; }
      const alcance = ids ? `${ids.length} ROS seleccionado(s)` : 'TODOS los ROS';
      if (!window.confirm(`Se van a CERRAR ${n} caso(s) abierto(s) del mismo cliente, cubiertos por ${alcance}. Se les agrega la nota "Cubierto por ROS". ¿Continuar?`)) return;
      const res = await api.post('/ros/cerrar-casos-cubiertos', { ros_ids: ids });
      toast.success(res.data?.message || `${res.data?.cerrados} caso(s) cerrado(s)`);
      loadRosList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cerrar casos cubiertos');
    } finally {
      setCerrandoCubiertos(false);
    }
  };


  const handleCierreDefinitivo = async () => {
    const acciones = {
      ambas: { eliminar_cuenta: true, marcar_enviado: true },
      marcar: { eliminar_cuenta: false, marcar_enviado: true },
      eliminar: { eliminar_cuenta: true, marcar_enviado: false },
    }[cierreAccion];
    setCierreRunning(true);
    try {
      const res = await api.post('/ros/cierre-definitivo-bulk', { ros_ids: rosSelectedIds, ...acciones });
      const { encolados = [], omitidos = [] } = res.data || {};
      if (omitidos.length) omitidos.slice(0, 4).forEach((o) => toast.warning(`${o.ros_id}: ${o.motivo}`));
      if (!encolados.length) { toast.error('Ningún ROS apto'); setCierreRunning(false); return; }
      toast.success(`${encolados.length} ROS en cierre definitivo`);
      setCierreModalOpen(false);
      setSelectedRosIds({});
      setCierreProgressItems(encolados.map((id) => ({ ros_id: id, estado: 'PROCESANDO', progreso: null })));
      setCierreProgressOpen(true);
      const poll = setInterval(async () => {
        try {
          const results = await Promise.all(
            encolados.map((id) =>
              api.get(`/ros/${id}/cierre-progreso`).then((r) => ({ ros_id: id, ...r.data })).catch(() => ({ ros_id: id }))
            )
          );
          setCierreProgressItems(results.map((r) => ({ ros_id: r.ros_id, estado: r.estado, progreso: r.cierre_progreso || null })));
          const allDone = results.every((r) => {
            const p = r.cierre_progreso || {};
            return p.estado === 'done' || p.estado === 'error' || p.estado === 'omitido';
          });
          if (allDone) { clearInterval(poll); loadRosList(); }
        } catch { /* ignore */ }
      }, 2500);
      setTimeout(() => { clearInterval(poll); loadRosList(); }, 900000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en cierre definitivo');
    } finally { setCierreRunning(false); }
  };

  // ====== Edit UIF Form ======
  const startEditUif = () => {
    setUifForm(JSON.parse(JSON.stringify(selectedRos.formulario_uif || {})));
    setEditingUif(true);
  };

  const saveUifForm = async () => {
    try {
      const res = await api.put(`/ros/${selectedRos.id}/formulario-uif`, uifForm);
      toast.success('Formulario UIF actualizado');
      setSelectedRos(res.data);
      setEditingUif(false);
      loadRosList();
    } catch { toast.error('Error guardando'); }
  };

  const updateUifField = (section, field, value) => {
    setUifForm(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: value }
    }));
  };

  const getEstadoBadge = (estado) => {
    const config = ESTADO_CONFIG[estado] || ESTADO_CONFIG.BORRADOR;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon size={12} />
        {ESTADO_LABELS[estado] || estado}
      </Badge>
    );
  };

  const filteredRos = rosList.filter(ros => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return ros.id.toLowerCase().includes(search) ||
           ros.titulo?.toLowerCase().includes(search) ||
           ros.numero_uif?.toLowerCase().includes(search) ||
           ros.nro_operacion_uif?.toLowerCase().includes(search) ||
           ros.caso_titulo?.toLowerCase().includes(search) ||
           ros.razon_social?.toLowerCase().includes(search) ||
           ros.cliente?.nombre?.toLowerCase().includes(search);
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="text-purple-600" />
            Reportes de Operaciones Sospechosas (ROS)
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de reportes para la UIF
          </p>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button onClick={openGenerateDialog} data-testid="generate-ros-btn">
            <Plus size={16} className="mr-2" />
            Generar ROS
          </Button>
          <Button onClick={() => { setSourceRos(null); setManualOpen(true); }} variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="nuevo-ros-manual-btn">
            <Plus size={16} className="mr-2" />
            Nuevo ROS Manual
          </Button>
          <Button onClick={() => navigate('/m/gestion-de-casos/ros-generar-lote')} variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="generar-lote-btn">
            <Shield size={16} className="mr-2" />
            Generar por Inusualidad
          </Button>
          <Button onClick={loadData} variant="outline">
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Total ROS</p>
                  <p className="text-3xl font-bold text-purple-700">{stats.total}</p>
                </div>
                <Shield className="text-purple-400" size={40} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Pendientes</p>
                  <p className="text-3xl font-bold text-amber-700">{stats.pendientes_aprobacion}</p>
                </div>
                <Clock className="text-amber-400" size={40} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Aprobados</p>
                  <p className="text-3xl font-bold text-green-700">{stats.por_estado?.APROBADO || 0}</p>
                </div>
                <CheckCircle className="text-green-400" size={40} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Enviados UIF</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.por_estado?.ENVIADO || 0}</p>
                </div>
                <Send className="text-blue-400" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Buscar por ID, caso o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="BORRADOR">Borrador</SelectItem>
                  <SelectItem value="EN_REVISION">En Revisión</SelectItem>
                  <SelectItem value="PENDIENTE_APROBACION">Pendiente Aprobación</SelectItem>
                  <SelectItem value="APROBADO">Aprobado</SelectItem>
                  <SelectItem value="ENVIADO">Enviado a UIF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {tiposRos.map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROS Table */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle>Lista de ROS ({total})</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleBulkSend}
              disabled={bulkSending || rosSelectedIds.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              size="sm"
              data-testid="bulk-send-uif-btn"
            >
              {bulkSending ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
              Enviar a UIF ({rosSelectedIds.length})
            </Button>
            <Button
              onClick={() => setCierreModalOpen(true)}
              disabled={rosSelectedIds.length === 0}
              variant="outline"
              className="border-green-600 text-green-700 hover:bg-green-50"
              size="sm"
              data-testid="cierre-definitivo-btn"
            >
              <CheckCircle size={14} className="mr-1" />
              Cierre definitivo ({rosSelectedIds.length})
            </Button>
            <Button
              onClick={handleCerrarCubiertos}
              disabled={cerrandoCubiertos}
              variant="outline"
              className="border-violet-600 text-violet-700 hover:bg-violet-50"
              size="sm"
              data-testid="cerrar-cubiertos-btn"
              title="Cierra los otros casos abiertos del mismo cliente que ya están cubiertos por un ROS"
            >
              {cerrandoCubiertos ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
              Cerrar casos cubiertos {rosSelectedIds.length > 0 ? `(${rosSelectedIds.length} ROS)` : '(todos)'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-40">ID</TableHead>
                <TableHead>Caso / Cliente</TableHead>
                <TableHead className="w-28">Monto</TableHead>
                <TableHead className="w-28">Estado</TableHead>
                <TableHead className="w-36">Fecha</TableHead>
                <TableHead className="w-32 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    No hay ROS para mostrar
                  </TableCell>
                </TableRow>
              ) : (
                filteredRos.map(ros => (
                  <TableRow key={ros.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={!!selectedRosIds[ros.id]}
                        onChange={() => toggleRosSel(ros.id)}
                        className="cursor-pointer"
                        data-testid={`ros-sel-${ros.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">
                      {ros.id}
                      {(ros.numero_uif || ros.nro_operacion_uif) && (
                        <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-300 text-[10px] font-semibold"
                          title="Número de presentación UIF" data-testid={`ros-numero-uif-${ros.id}`}>
                          UIF Nº {ros.numero_uif || ros.nro_operacion_uif}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-sm text-slate-900 truncate max-w-xs">{ros.titulo || ros.caso_titulo || ros.caso_id || (ros.es_manual ? 'ROS Manual' : '-')}</p>
                      <p className="text-xs text-slate-400">{ros.razon_social || ros.cliente?.nombre || '-'} {ros.cuit ? `(${ros.cuit})` : ''}</p>
                    </TableCell>
                    <TableCell className="font-medium text-sm">
                      {formatCurrency(ros.operacion?.monto_total || ros.formulario_uif?.operaciones_productos?.monto || 0, ros.operacion?.moneda)}
                    </TableCell>
                    <TableCell>
                      {getEstadoBadge(ros.estado)}
                      {ros.estado === 'BORRADOR' && ros.ultimo_error_uif && (
                        <p className="text-[10px] text-red-500 mt-1 max-w-[180px] leading-tight" title={ros.ultimo_error_uif}
                          data-testid={`ros-error-${ros.id}`}>
                          ⚠ {ros.ultimo_error_uif.length > 70 ? ros.ultimo_error_uif.slice(0, 70) + '…' : ros.ultimo_error_uif}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {formatDate(ros.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Copiar CVU/CBU"
                          onClick={() => copyCvu(ros.formulario_uif?.operaciones_productos?.nro_identificacion || ros.operacion?.cvu || ros.cvu)}
                          data-testid={`copy-cvu-${ros.id}`}
                        >
                          <Copy size={14} className="mr-1" /> CVU
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/m/gestion-de-casos/ros-detalle/${ros.id}`)} data-testid={`view-ros-${ros.id}`}>
                          <Eye size={14} className="mr-1" /> Ver
                        </Button>
                        <Button variant="ghost" size="sm" title="Duplicar este ROS (crear otro con los mismos textos)"
                          onClick={() => handleDuplicarRos(ros.id)} data-testid={`dup-ros-${ros.id}`}>
                          <CopyPlus size={14} className="mr-1" /> Duplicar
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteRos(ros.id)} data-testid={`del-ros-${ros.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Barra de progreso del envío masivo a UIF */}
      <Dialog open={bulkProgressOpen} onOpenChange={setBulkProgressOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }} data-testid="bulk-progress-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send size={18} className="text-indigo-600" /> Envío masivo a UIF — Progreso
            </DialogTitle>
            <DialogDescription>
              Se procesan de a uno. Podés cerrar esta ventana; el envío sigue en segundo plano.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {bulkProgressItems.map((it) => {
              const prog = it.progreso || {};
              const isErr = prog.estado === 'error' || it.estado === 'BORRADOR';
              const isOk = it.estado === 'EN_REVISION';
              return (
                <div key={it.ros_id} className={`rounded-lg border p-3 ${isErr ? 'border-red-200 bg-red-50/40' : isOk ? 'border-green-200 bg-green-50/40' : 'border-indigo-200 bg-indigo-50/40'}`} data-testid={`bulk-progress-${it.ros_id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-xs text-slate-700">{it.ros_id}</span>
                    <span className="flex items-center gap-1 text-xs">
                      {isOk ? <CheckCircle size={14} className="text-green-600" />
                        : isErr ? <XCircle size={14} className="text-red-500" />
                        : <RefreshCw size={14} className="text-indigo-500 animate-spin" />}
                      {isOk ? `OK · UIF ${it.nro || ''}` : isErr ? 'Error' : 'Procesando…'}
                    </span>
                  </div>
                  <p className={`text-xs ${isErr ? 'text-red-600' : 'text-slate-600'}`}>{prog.mensaje || 'En cola…'}</p>
                  {prog.pasos && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {prog.pasos.map((p) => (
                        <span key={p.key} className={`text-[10px] px-1.5 py-0.5 rounded ${p.estado === 'done' ? 'bg-green-100 text-green-700' : p.estado === 'running' ? 'bg-indigo-100 text-indigo-700' : p.estado === 'error' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400'}`} title={p.detalle || ''}>
                          {p.label.replace('Cargar ', '').replace('Iniciar sesión en el portal ', '').replace('Abrir formulario de ', '')}
                        </span>
                      ))}
                    </div>
                  )}
                  {isErr && prog.pasos && (() => {
                    const errStep = prog.pasos.find((p) => p.estado === 'error');
                    return errStep?.detalle ? <p className="text-[11px] text-red-500 mt-1 break-words">{errStep.detalle}</p> : null;
                  })()}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkProgressOpen(false)} data-testid="bulk-progress-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de opciones del Cierre Definitivo */}
      <Dialog open={cierreModalOpen} onOpenChange={setCierreModalOpen}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }} data-testid="cierre-options-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={18} className="text-green-600" /> Cierre definitivo — {rosSelectedIds.length} ROS
            </DialogTitle>
            <DialogDescription>
              El cierre definitivo busca la cuenta en Waya por CVU y, <b>solo si está BLOQUEADA</b>, da de baja el CVU.
              Si no está bloqueada, se omite sin marcar. Elegí qué ejecutar:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {[
              { v: 'ambas', t: 'Marcar enviado + eliminar cuenta en Waya', d: 'Flujo completo (recomendado)' },
              { v: 'marcar', t: 'Solo marcar como enviado', d: 'No toca Waya' },
              { v: 'eliminar', t: 'Solo eliminar cuenta en Waya', d: 'No marca el ROS' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setCierreAccion(o.v)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${cierreAccion === o.v ? 'border-green-500 bg-green-50 ring-1 ring-green-300' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid={`cierre-opt-${o.v}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`h-3.5 w-3.5 rounded-full border-2 ${cierreAccion === o.v ? 'border-green-600 bg-green-600' : 'border-slate-300'}`} />
                  <span className="text-sm font-semibold text-slate-800">{o.t}</span>
                </div>
                <p className="text-xs text-slate-500 ml-5.5 mt-0.5">{o.d}</p>
              </button>
            ))}
          </div>
          {(cierreAccion === 'ambas' || cierreAccion === 'eliminar') && (
            <p className="text-xs text-red-600 bg-red-50 rounded p-2 mt-1">
              ⚠️ La baja del CVU en Waya es irreversible. Solo se ejecuta si la cuenta está BLOQUEADA.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCierreModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCierreDefinitivo} disabled={cierreRunning} className="bg-green-600 hover:bg-green-700 text-white" data-testid="cierre-ejecutar-btn">
              {cierreRunning ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
              Ejecutar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Barra de progreso del Cierre Definitivo */}
      <Dialog open={cierreProgressOpen} onOpenChange={setCierreProgressOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }} data-testid="cierre-progress-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={18} className="text-green-600" /> Cierre definitivo — Progreso
            </DialogTitle>
            <DialogDescription>Se procesan de a uno en Waya. Podés cerrar esta ventana; sigue en segundo plano.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {cierreProgressItems.map((it) => {
              const prog = it.progreso || {};
              const isErr = prog.estado === 'error';
              const isOmit = prog.estado === 'omitido';
              const isOk = prog.estado === 'done';
              return (
                <div key={it.ros_id} className={`rounded-lg border p-3 ${isErr ? 'border-red-200 bg-red-50/40' : isOmit ? 'border-amber-200 bg-amber-50/40' : isOk ? 'border-green-200 bg-green-50/40' : 'border-indigo-200 bg-indigo-50/40'}`} data-testid={`cierre-progress-${it.ros_id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-xs text-slate-700">{it.ros_id} {prog.cvu ? `· CVU ${prog.cvu}` : ''}</span>
                    <span className="flex items-center gap-1 text-xs">
                      {isOk ? <CheckCircle size={14} className="text-green-600" />
                        : isErr ? <XCircle size={14} className="text-red-500" />
                        : isOmit ? <AlertCircle size={14} className="text-amber-500" />
                        : <RefreshCw size={14} className="text-indigo-500 animate-spin" />}
                      {isOk ? 'Completado' : isErr ? 'Error' : isOmit ? 'Omitido' : 'Procesando…'}
                    </span>
                  </div>
                  <p className={`text-xs ${isErr ? 'text-red-600' : isOmit ? 'text-amber-700' : 'text-slate-600'}`}>{prog.mensaje || 'En cola…'}</p>
                  {prog.pasos && (
                    <div className="space-y-1 mt-2">
                      {prog.pasos.map((p) => (
                        <div key={p.key} className="flex items-start gap-2">
                          {p.estado === 'done' ? <CheckCircle size={13} className="text-green-500 mt-0.5 shrink-0" />
                            : p.estado === 'running' ? <RefreshCw size={13} className="text-indigo-500 animate-spin mt-0.5 shrink-0" />
                            : p.estado === 'error' ? <XCircle size={13} className="text-red-500 mt-0.5 shrink-0" />
                            : <span className="h-3 w-3 rounded-full border border-slate-300 mt-0.5 shrink-0" />}
                          <div className="min-w-0">
                            <span className={`text-xs ${p.estado === 'error' ? 'text-red-600' : 'text-slate-700'}`}>{p.label}</span>
                            {p.detalle && <p className={`text-[11px] break-words ${p.estado === 'error' ? 'text-red-500' : 'text-slate-400'}`}>{p.detalle}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCierreProgressOpen(false)} data-testid="cierre-progress-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="text-purple-600" />
              {selectedRos?.id}
            </DialogTitle>
            <DialogDescription>
              Detalle del Reporte de Operación Sospechosa
            </DialogDescription>
          </DialogHeader>
          
          {selectedRos && (
            <Tabs value={selectedRos._activeTab || 'info'} onValueChange={v => setSelectedRos(prev => ({...prev, _activeTab: v}))} className="mt-4">
              <TabsList>
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="cliente">Cliente</TabsTrigger>
                <TabsTrigger value="operacion">Operación</TabsTrigger>
                {selectedRos.formulario_uif && <TabsTrigger value="formulario">Formulario UIF</TabsTrigger>}
                <TabsTrigger value="historial">Historial</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Estado</Label>
                    {getEstadoBadge(selectedRos.estado)}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tipo</Label>
                    <p className="font-medium">
                      {tiposRos.find(t => t.id === selectedRos.tipo)?.nombre || selectedRos.tipo}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Caso Asociado</Label>
                    <p className="font-medium">{selectedRos.caso_titulo || selectedRos.caso_id}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Descripción de la Sospecha</Label>
                    <p className="text-sm bg-slate-50 p-3 rounded-lg mt-1">
                      {selectedRos.descripcion_sospecha}
                    </p>
                  </div>
                  
                  {/* Indicadores de Alerta */}
                  {selectedRos.indicadores_alerta?.length > 0 && (
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Indicadores de Alerta</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedRos.indicadores_alerta.map((ind, idx) => (
                          <Badge key={idx} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            <AlertTriangle size={12} className="mr-1" />
                            {ind.nombre}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Score de Riesgo */}
                  {selectedRos.score_riesgo?.score_total && (
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Score de Riesgo</Label>
                      <div 
                        className="mt-2 p-4 rounded-lg flex items-center gap-4"
                        style={{ backgroundColor: `${selectedRos.score_riesgo.color}15` }}
                      >
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold"
                          style={{ backgroundColor: selectedRos.score_riesgo.color }}
                        >
                          {selectedRos.score_riesgo.score_total}
                        </div>
                        <div>
                          <p className="font-medium">Riesgo {selectedRos.score_riesgo.nivel_riesgo}</p>
                          <p className="text-sm text-slate-500">
                            {selectedRos.score_riesgo.cantidad_factores_aplicados} factores detectados
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Aprobaciones */}
                {selectedRos.aprobaciones?.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Aprobaciones</Label>
                    <div className="space-y-2">
                      {selectedRos.aprobaciones.map((apr, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg ${apr.decision === 'APROBADO' ? 'bg-green-50' : 'bg-red-50'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {apr.decision === 'APROBADO' ? 
                                <CheckCircle className="text-green-600" size={16} /> :
                                <XCircle className="text-red-600" size={16} />
                              }
                              <span className="font-medium">Nivel {apr.nivel}: {apr.decision}</span>
                            </div>
                            <span className="text-xs text-slate-500">{formatDate(apr.fecha)}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{apr.aprobador_email}</p>
                          {apr.comentario && (
                            <p className="text-sm italic mt-1">"{apr.comentario}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="cliente" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Nombre</Label>
                    <p className="font-medium">{selectedRos.cliente?.nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">CUIT</Label>
                    <p className="font-mono">{selectedRos.cliente?.cuit || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Documento</Label>
                    <p>{selectedRos.cliente?.documento || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tipo</Label>
                    <p>{selectedRos.cliente?.tipo || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Domicilio</Label>
                    <p>{selectedRos.cliente?.domicilio || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Actividad</Label>
                    <p>{selectedRos.cliente?.actividad || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">PEP</Label>
                    <Badge className={selectedRos.cliente?.pep ? 'bg-red-100 text-red-700' : 'bg-slate-100'}>
                      {selectedRos.cliente?.pep ? 'Sí' : 'No'}
                    </Badge>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="operacion" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Tipo de Operación</Label>
                    <p className="font-medium">{selectedRos.operacion?.tipo || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Monto Total</Label>
                    <p className="text-xl font-bold text-green-700">
                      {formatCurrency(selectedRos.operacion?.monto_total, selectedRos.operacion?.moneda)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Cantidad de Operaciones</Label>
                    <p>{selectedRos.operacion?.cantidad_operaciones || 1}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Período</Label>
                    <p>
                      {selectedRos.operacion?.fecha_inicio ? formatDate(selectedRos.operacion.fecha_inicio) : '-'}
                      {' → '}
                      {selectedRos.operacion?.fecha_fin ? formatDate(selectedRos.operacion.fecha_fin) : '-'}
                    </p>
                  </div>
                  
                  {selectedRos.operacion?.cuentas_involucradas?.length > 0 && (
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Cuentas Involucradas</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedRos.operacion.cuentas_involucradas.map((cuenta, idx) => (
                          <Badge key={idx} variant="outline">{cuenta}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Formulario UIF Tab */}
              <TabsContent value="formulario" className="mt-4 space-y-4">
                {selectedRos.formulario_uif ? (
                  <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">Datos que se cargarán en el portal SRO+ de la UIF</p>
                    {!editingUif ? (
                      <Button variant="outline" size="sm" onClick={startEditUif} data-testid="edit-uif-btn">
                        <Edit2 size={14} className="mr-1" /> Editar
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveUifForm} data-testid="save-uif-btn">
                          <Save size={14} className="mr-1" /> Guardar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditingUif(false)}>Cancelar</Button>
                      </div>
                    )}
                  </div>

                  {/* Persona Física */}
                  {Object.keys(selectedRos.formulario_uif.persona_fisica || {}).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <User size={14} /> Persona Física
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          ['nombre', 'Nombre'], ['apellido', 'Apellido'], ['cuit_cdi', 'CUIT'],
                          ['nro_documento', 'Nro Doc'], ['tipo_documento', 'Tipo Doc'], ['fecha_nacimiento', 'F. Nacimiento'],
                          ['sexo', 'Sexo'], ['nacionalidad', 'Nacionalidad'], ['calle', 'Calle'],
                          ['nro', 'Nro'], ['localidad', 'Localidad'], ['provincia', 'Provincia'],
                          ['codigo_postal', 'CP'], ['pais', 'País'], ['email', 'Email'],
                          ['actividad', 'Actividad'], ['estado_civil', 'Estado Civil'], ['relacion_hecho', 'Relación Hecho'],
                        ].map(([k, label]) => (
                          <div key={k} className="text-xs">
                            <span className="text-slate-400 block">{label}</span>
                            {editingUif ? (
                              <Input className="h-7 text-xs mt-0.5" value={(uifForm.persona_fisica || {})[k] || ''} onChange={e => updateUifField('persona_fisica', k, e.target.value)} />
                            ) : (
                              <span className="text-slate-700">{(selectedRos.formulario_uif.persona_fisica || {})[k] || '-'}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Persona Jurídica */}
                  {Object.keys(selectedRos.formulario_uif.persona_juridica || {}).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                        <Building2 size={14} /> Persona Jurídica
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          ['denominacion', 'Denominación'], ['cuit_cdi', 'CUIT'], ['calle', 'Calle'],
                          ['nro', 'Nro'], ['localidad', 'Localidad'], ['provincia', 'Provincia'],
                          ['codigo_postal', 'CP'], ['pais', 'País'], ['email', 'Email'],
                          ['actividad', 'Actividad'], ['relacion_hecho', 'Relación Hecho'],
                        ].map(([k, label]) => (
                          <div key={k} className="text-xs">
                            <span className="text-slate-400 block">{label}</span>
                            {editingUif ? (
                              <Input className="h-7 text-xs mt-0.5" value={(uifForm.persona_juridica || {})[k] || ''} onChange={e => updateUifField('persona_juridica', k, e.target.value)} />
                            ) : (
                              <span className="text-slate-700">{(selectedRos.formulario_uif.persona_juridica || {})[k] || '-'}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Operaciones y Productos */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                      <DollarSign size={14} /> Operaciones y Productos
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        ['fecha_inicio', 'Fecha Inicio'], ['fecha_fin', 'Fecha Fin'], ['monto', 'Monto'],
                        ['moneda', 'Moneda'], ['producto', 'Producto'], ['nro_identificacion', 'Nro ID (CVU)'],
                        ['tipo_inusualidad', 'Tipo Inusualidad'], ['localidad_1', 'Localidad'], ['provincia_1', 'Provincia'],
                      ].map(([k, label]) => (
                        <div key={k} className="text-xs">
                          <span className="text-slate-400 block">{label}</span>
                          {editingUif ? (
                            <Input className="h-7 text-xs mt-0.5" value={String((uifForm.operaciones_productos || {})[k] || '')} onChange={e => updateUifField('operaciones_productos', k, e.target.value)} />
                          ) : (
                            <span className="text-slate-700">{String((selectedRos.formulario_uif.operaciones_productos || {})[k] || '-')}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Textos */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                      <FileText size={14} /> Textos del ROS
                    </h4>
                    <div className="space-y-3">
                      {[
                        ['descripcion_operatoria', 'Descripción Operatoria'],
                        ['analisis_so', 'Análisis del SO'],
                        ['documentacion_respaldo', 'Documentación de Respaldo'],
                        ['conclusiones', 'Conclusiones'],
                      ].map(([k, label]) => (
                        <div key={k}>
                          <span className="text-xs text-slate-400 font-semibold">{label}</span>
                          {editingUif ? (
                            <Textarea className="text-xs mt-0.5 min-h-[60px]" value={(uifForm.textos || {})[k] || ''} onChange={e => updateUifField('textos', k, e.target.value)} />
                          ) : (
                            <p className="text-xs text-slate-700 bg-slate-50 rounded p-2 mt-0.5 min-h-[40px] whitespace-pre-wrap">
                              {(selectedRos.formulario_uif.textos || {})[k] || <span className="text-slate-300 italic">Sin contenido</span>}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-400 py-4">Este ROS no tiene formulario UIF. Generá uno nuevo desde un caso.</p>
                )}
              </TabsContent>
              
              <TabsContent value="historial" className="mt-4">
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedRos.historial?.map((evento, idx) => (
                    <div key={idx} className="flex gap-3 pb-3 border-b last:border-0">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <History size={14} className="text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{evento.accion}</p>
                        <p className="text-sm text-slate-500">{evento.detalle}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                          <span>{evento.usuario_email}</span>
                          <span>{formatDate(evento.fecha)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter className="flex gap-2 mt-4">
            {selectedRos?.estado === 'BORRADOR' && (
              <Button 
                onClick={() => handleSubmitForApproval(selectedRos.id)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Send size={16} className="mr-2" />
                Enviar para Aprobación
              </Button>
            )}
            
            {selectedRos?.estado === 'PENDIENTE_APROBACION' && (
              <>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-300"
                  onClick={() => {
                    setApprovalAction('RECHAZADO');
                    setApprovalComment('');
                    setShowApprovalDialog(true);
                  }}
                >
                  <XCircle size={16} className="mr-2" />
                  Rechazar
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => {
                    setApprovalAction('APROBADO');
                    setApprovalComment('');
                    setShowApprovalDialog(true);
                  }}
                >
                  <CheckCircle size={16} className="mr-2" />
                  Aprobar (Nivel {selectedRos.nivel_aprobacion_actual}/{selectedRos.niveles_aprobacion_requeridos})
                </Button>
              </>
            )}
            
            {selectedRos?.estado === 'APROBADO' && (
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setShowSendDialog(true)}
              >
                <Send size={16} className="mr-2" />
                Marcar como Enviado a UIF
              </Button>
            )}
            
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {approvalAction === 'APROBADO' ? (
                <CheckCircle className="text-green-600" />
              ) : (
                <XCircle className="text-red-600" />
              )}
              {approvalAction === 'APROBADO' ? 'Aprobar ROS' : 'Rechazar ROS'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>{approvalAction === 'RECHAZADO' ? 'Motivo del Rechazo *' : 'Comentario (opcional)'}</Label>
            <Textarea
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              placeholder={approvalAction === 'RECHAZADO' 
                ? 'Indique el motivo del rechazo...' 
                : 'Agregue un comentario si lo desea...'
              }
              rows={3}
              className="mt-2"
              style={{ backgroundColor: '#fff' }}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleApprovalDecision}
              disabled={submitting || (approvalAction === 'RECHAZADO' && !approvalComment.trim())}
              className={`text-white ${approvalAction === 'APROBADO' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {submitting ? 'Procesando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to UIF Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="text-blue-600" />
              Registrar Envío a UIF
            </DialogTitle>
            <DialogDescription>
              Ingrese el número de expediente asignado por la UIF
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Número UIF</Label>
            <Input
              value={numeroUif}
              onChange={(e) => setNumeroUif(e.target.value)}
              placeholder="Ej: UIF-2025-12345"
              className="mt-2"
              style={{ backgroundColor: '#fff' }}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendToUIF}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submitting ? 'Procesando...' : 'Confirmar Envío'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate ROS Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Generar nuevo ROS</DialogTitle>
            <DialogDescription>Seleccioná un caso y un template de texto</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Template selector */}
            <div>
              <Label className="text-sm font-medium">Template de textos</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="mt-1" data-testid="select-template">
                  <SelectValue placeholder="Sin template (textos vacíos)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin template</SelectItem>
                  {rosTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Case selector */}
            <div>
              <Label className="text-sm font-medium">Caso(s)</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="Buscar en TODOS los casos (título, CUIT, CVU, cliente)..." value={caseSearch} onChange={e => setCaseSearch(e.target.value)} className="flex-1" data-testid="case-search" />
                <Select value={genEstadoFiltro} onValueChange={setGenEstadoFiltro}>
                  <SelectTrigger className="w-44" data-testid="gen-estado-filter">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {CASE_ESTADOS.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1" data-testid="gen-cases-list">
              {casesLoading && <p className="text-sm text-slate-400 text-center py-3"><RefreshCw size={14} className="inline animate-spin mr-1" /> Buscando...</p>}
              {!casesLoading && cases.map(c => (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 border border-slate-100">
                  <input
                    type="checkbox"
                    checked={!!selectedGenCaseIds[c.id]}
                    onChange={() => toggleGenCase(c.id)}
                    className="cursor-pointer"
                    data-testid={`gen-check-${c.id}`}
                  />
                  <div className="flex-1 mr-2 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.titulo}</p>
                    <p className="text-xs text-slate-400">{c.nombre_padron || c.cuit || 'Sin CUIT'} &middot; {c.estado}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleGenerateRos(c.id)} disabled={generating || bulkGenerating} data-testid={`gen-ros-${c.id}`}>
                    {generating ? <RefreshCw size={12} className="animate-spin" /> : 'Generar'}
                  </Button>
                </div>
              ))}
              {!casesLoading && cases.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No se encontraron casos</p>}
            </div>
          </div>
          <DialogFooter className="mt-2">
            <span className="text-xs text-slate-500 mr-auto self-center">{genSelectedIds.length} seleccionado(s)</span>
            <Button
              onClick={handleBulkGenerate}
              disabled={bulkGenerating || genSelectedIds.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="bulk-generate-btn"
            >
              {bulkGenerating ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Shield size={14} className="mr-1" />}
              Generar seleccionados ({genSelectedIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ROSManualDialog
        open={manualOpen}
        onOpenChange={(v) => { setManualOpen(v); if (!v) setSourceRos(null); }}
        sourceRos={sourceRos}
        onCreated={(ros) => navigate(`/m/gestion-de-casos/ros-detalle/${ros.id}`)}
      />
    </div>
  );
}
